import { api } from "./config";
import { getEvidencePhotoUrl } from "./wallet";
import { evidenceUrl } from "./financial";
import { getPhotoUrl } from "utils";

// ── Photo Gallery (read-only) ────────────────────────────────────────────────

export async function getGalleryAlbums() {
  try {
    const res = await api().get("/gallery/albums");
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function getGalleryPhotos({ album, from, to, category, q, customerId, page = 1, limit = 48 }) {
  try {
    const params = new URLSearchParams({ album, page, limit });
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (category) params.append("category", category);
    if (q) params.append("q", q);
    if (customerId) params.append("customerId", customerId);
    const res = await api().get(`/gallery/photos?${params.toString()}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function getGalleryPackages({ from, to, category, q, customerId, page = 1, limit = 24 }) {
  try {
    const params = new URLSearchParams({ page, limit });
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (category) params.append("category", category);
    if (q) params.append("q", q);
    if (customerId) params.append("customerId", customerId);
    const res = await api().get(`/gallery/packages?${params.toString()}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function getGalleryItemPhotos(album, id) {
  try {
    const res = await api().get(`/gallery/item-photos?album=${album}&id=${id}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Resolve the display URL for a gallery photo row, per album:
//   packages -> static /uploads path; everything else -> authenticated
//   evidence endpoints (shipping/branchCash share uploads/shipping-evidence/,
//   wallet/purchase share uploads/wallet-evidence/ — see backend gallery.js THUMB_SOURCES).
export function galleryPhotoUrl(album, photo) {
  if (album === "packages") return getPhotoUrl(photo.path);
  if (album === "shipping" || album === "branchCash") return evidenceUrl(photo.filename);
  return getEvidencePhotoUrl(photo.filename);
}

// 320px cached thumbnail for grid tiles (always authenticated, all albums).
// Lightbox/drawers keep using galleryPhotoUrl for the full-res original.
export function galleryThumbUrl(album, photo) {
  const file = album === "packages" ? photo.path : photo.filename;
  if (!file) return "";
  const base = api().defaults.baseURL || "";
  return `${base}/gallery/thumb?album=${album}&file=${encodeURIComponent(file)}`;
}

// Guarded endpoints (shipping/wallet/purchase/branchCash) need the request to carry
// the auth cookie and can't be loaded by a bare <img>. packages photos are public statics.
export const isAuthenticatedAlbum = (album) => album !== "packages";

// LINE-style "download all": fetch each photo as a blob and trigger one
// anchor-download per file — no zip. Guarded endpoints go through the axios
// instance (cookies); static uploads through fetch. Returns count saved.
export async function downloadPhotos(album, photos, baseName, onProgress) {
  let saved = 0;
  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    const url = galleryPhotoUrl(album, photo);
    try {
      let blob;
      if (isAuthenticatedAlbum(album)) {
        const path = url.replace(api().defaults.baseURL || "", "");
        const res = await api().get(path, { responseType: "blob" });
        blob = res.data;
      } else {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        blob = await res.blob();
      }
      const ext = (url.split(".").pop() || "jpg").split("?")[0].slice(0, 5);
      const name = photos.length > 1 ? `${baseName}_${i + 1}.${ext}` : `${baseName}.${ext}`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      saved++;
      onProgress?.(saved, photos.length);
    } catch (e) {
      console.error("Failed to download photo:", url, e);
    }
  }
  return saved;
}
